import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertCircle, CheckCircle2, Frown, Meh, MessageSquare, Smile, ThumbsDown, ThumbsUp, } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
// ---------- Tone + label tables ---------------------------------------
//
// Each axis has its own customer-facing wording (matches the form the
// client filled in). The backend stores the canonical 5-point enum, so
// we translate it back here for display.
const TONE = {
    excellent: {
        text: 'text-emerald-700 dark:text-emerald-300',
        bg: 'bg-emerald-50 dark:bg-emerald-950/30',
        pill: 'bg-emerald-100 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950/50 dark:text-emerald-300',
        Icon: ThumbsUp,
    },
    good: {
        text: 'text-sky-700 dark:text-sky-300',
        bg: 'bg-sky-50 dark:bg-sky-950/30',
        pill: 'bg-sky-100 text-sky-700 ring-sky-600/20 dark:bg-sky-950/50 dark:text-sky-300',
        Icon: Smile,
    },
    average: {
        text: 'text-amber-700 dark:text-amber-300',
        bg: 'bg-amber-50 dark:bg-amber-950/30',
        pill: 'bg-amber-100 text-amber-700 ring-amber-600/20 dark:bg-amber-950/50 dark:text-amber-300',
        Icon: Meh,
    },
    poor: {
        text: 'text-rose-700 dark:text-rose-300',
        bg: 'bg-rose-50 dark:bg-rose-950/30',
        pill: 'bg-rose-100 text-rose-700 ring-rose-600/20 dark:bg-rose-950/50 dark:text-rose-300',
        Icon: ThumbsDown,
    },
    very_poor: {
        text: 'text-rose-800 dark:text-rose-200',
        bg: 'bg-rose-100 dark:bg-rose-950/50',
        pill: 'bg-rose-200 text-rose-900 ring-rose-700/30 dark:bg-rose-950/70 dark:text-rose-200',
        Icon: Frown,
    },
};
// Per-axis label translation — mirrors what the customer saw in the form.
const OVERALL_LABEL = {
    excellent: 'Very Satisfied',
    good: 'Satisfied',
    average: 'Neutral',
    poor: 'Dissatisfied',
    very_poor: 'Very Dissatisfied',
};
const FOUR_POINT_LABEL = {
    excellent: 'Excellent',
    good: 'Good',
    average: 'Average',
    poor: 'Poor',
    very_poor: 'Poor', // shouldn't occur on 4-point axes; safe fallback
};
const RESOLUTION_LABEL = {
    excellent: 'Yes — completely',
    good: 'Yes — completely',
    average: 'Partially',
    poor: 'No',
    very_poor: 'No',
};
// ---------- The card ---------------------------------------------------
export function FeedbackResultCard({ ticketId }) {
    const q = useQuery({
        queryKey: ['ticket', ticketId, 'feedback'],
        queryFn: () => api.get(`/tickets/${ticketId}/feedback`),
        enabled: !!ticketId,
    });
    if (q.isLoading) {
        return (_jsx(Card, { children: _jsx(CardBody, { children: _jsx("div", { className: "h-32 animate-pulse rounded bg-slate-100 dark:bg-slate-800/50" }) }) }));
    }
    if (q.isError) {
        return (_jsx(Card, { children: _jsx(CardBody, { children: _jsxs("div", { className: "flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300", children: [_jsx(AlertCircle, { className: "mt-0.5 h-4 w-4 shrink-0" }), _jsxs("span", { children: ["Failed to load feedback. ", q.error?.message] })] }) }) }));
    }
    const fb = q.data;
    if (!fb) {
        // Defensive — caller should have gated on `feedback_submitted` already.
        return null;
    }
    const metrics = [
        { label: 'Overall Satisfaction', value: fb.overall_satisfaction, display: OVERALL_LABEL[fb.overall_satisfaction] },
        { label: 'Response Time', value: fb.response_time, display: FOUR_POINT_LABEL[fb.response_time] },
        { label: 'Issue Resolved?', value: fb.resolution_quality, display: RESOLUTION_LABEL[fb.resolution_quality] },
        { label: 'Staff Performance', value: fb.staff_performance, display: FOUR_POINT_LABEL[fb.staff_performance] },
        { label: 'Communication', value: fb.communication, display: FOUR_POINT_LABEL[fb.communication] },
    ];
    // Headline tone derived from overall satisfaction so the card edge matches
    // the customer's verdict at a glance.
    const headlineTone = TONE[fb.overall_satisfaction];
    return (_jsxs(Card, { className: cn('border-2', headlineToBorder(fb.overall_satisfaction)), children: [_jsx(CardHeader, { children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', headlineTone.bg, headlineTone.text), children: _jsx(headlineTone.Icon, { size: 20 }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "text-base font-semibold text-slate-900 dark:text-slate-100", children: "Client Feedback Result" }), _jsxs("p", { className: "mt-0.5 text-xs text-slate-500", children: ["Submitted ", format(new Date(fb.created_at), 'PPp')] })] }), _jsxs("span", { className: cn('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset', headlineTone.pill), children: [_jsx(CheckCircle2, { size: 11 }), OVERALL_LABEL[fb.overall_satisfaction]] })] }) }), _jsxs(CardBody, { className: "space-y-3", children: [_jsx("dl", { className: "divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700", children: metrics.map((m) => {
                            const tone = TONE[m.value];
                            return (_jsxs("div", { className: "flex items-center gap-3 px-3 py-2 text-sm", children: [_jsx("div", { className: cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', tone.bg, tone.text), children: _jsx(tone.Icon, { size: 14 }) }), _jsx("dt", { className: "flex-1 text-slate-700 dark:text-slate-300", children: m.label }), _jsx("dd", { children: _jsx("span", { className: cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset', tone.pill), children: m.display }) })] }, m.label));
                        }) }), fb.comment && (_jsxs("div", { className: "rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/30", children: [_jsxs("div", { className: "mb-1 flex items-center gap-1.5 text-tag uppercase tracking-wider text-slate-500", children: [_jsx(MessageSquare, { size: 11 }), "Customer comment"] }), _jsxs("div", { className: "whitespace-pre-wrap italic text-slate-700 dark:text-slate-300", children: ["\u201C", fb.comment, "\u201D"] })] }))] })] }));
}
// Match the headline border to the overall verdict so even a quick
// glance at the side rail conveys the result.
function headlineToBorder(r) {
    switch (r) {
        case 'excellent': return 'border-emerald-300 dark:border-emerald-800';
        case 'good': return 'border-sky-300 dark:border-sky-800';
        case 'average': return 'border-amber-300 dark:border-amber-800';
        case 'poor': return 'border-rose-300 dark:border-rose-800';
        case 'very_poor': return 'border-rose-400 dark:border-rose-700';
    }
}
